import React, { memo } from 'react';
import { View, StyleSheet } from 'react-native';

import AppStyles from '../AppStyles';

import CustomText from './CustomText';
import { useTranslation } from '../i18n';

/**
 * StatusBadge - Displays report status with color coding
 *
 * Memoized to prevent unnecessary re-renders in lists.
 *
 * @param {string} status - Report status: 'Open' | 'InProgress' | 'Closed'
 * @param {object} style - Additional styles for the badge
 * @param {boolean} compact - Use smaller size for list views
 */
const StatusBadge = memo(function StatusBadge({ status, style, compact = false }) {
  // useTranslation rather than the module-level `t`: this component is memoized,
  // so it needs its own subscription to re-render when the language changes.
  const { t } = useTranslation('reports');

  const getStatusConfig = () => {
    switch (status) {
      case 'Open':
        return {
          backgroundColor: AppStyles.colorSet.warningLight,
          textColor: '#B45309', // Darker orange for better contrast
          label: t('status.Open'),
        };
      case 'InProgress':
        return {
          backgroundColor: AppStyles.colorSet.infoLight,
          textColor: AppStyles.colorSet.info,
          label: t('status.InProgress'),
        };
      case 'Closed':
        return {
          backgroundColor: AppStyles.colorSet.successLight,
          textColor: AppStyles.colorSet.success,
          label: t('status.Closed'),
        };
      default:
        return {
          backgroundColor: AppStyles.colorSet.inputBackground,
          textColor: AppStyles.colorSet.textSecondary,
          label: status || t('status.unknown'),
        };
    }
  };

  const config = getStatusConfig();

  return (
    <View
      style={[
        compact ? styles.badgeCompact : styles.badge,
        { backgroundColor: config.backgroundColor },
        style,
      ]}
    >
      <CustomText font="medium" size={compact ? 11 : 14} color={config.textColor}>
        {config.label}
      </CustomText>
    </View>
  );
});

const styles = StyleSheet.create({
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: AppStyles.borderRadii.pill,
    alignSelf: 'flex-start',
  },
  badgeCompact: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: AppStyles.borderRadii.pill,
    alignSelf: 'flex-start',
  },
});

export default StatusBadge;
